import React from "react";
import { Box, Container, Typography, Grid, Button } from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import StoreProductCard from "./Card";
import { Product } from "../manage-products/manageProducts";
import { StoreThemeTokens, getStoreTheme } from "./themes/storeThemes";

interface StoreProductPageProps {
  products: Product[];
  handleOrder: (product: Product) => void;
  storeData?: any;
  themeTokens?: StoreThemeTokens;
  searchQuery?: string;
  selectedCategory?: string;
  onResetFilter?: () => void;
}

const StoreProductPage: React.FC<StoreProductPageProps> = ({
  products = [],
  handleOrder,
  storeData,
  themeTokens: propTokens,
  searchQuery = "",
  selectedCategory = "All",
  onResetFilter,
}) => {
  const themeTokens = propTokens || getStoreTheme(storeData?.theme);
  const storePhone = storeData?.number || storeData?.header?.phone_number;
  const storeName = storeData?.name || storeData?.header?.store_name;

  // Filter products by search query and category
  const filteredProducts = products.filter((p) => {
    const matchesSearch =
      !searchQuery.trim() ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory =
      !selectedCategory ||
      selectedCategory === "All" ||
      selectedCategory === "All Products" ||
      (p.category && p.category.toLowerCase().trim() === selectedCategory.toLowerCase().trim()) ||
      (p.category && p.category.toLowerCase().includes(selectedCategory.toLowerCase())) ||
      p.name.toLowerCase().includes(selectedCategory.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(selectedCategory.toLowerCase()));

    return matchesSearch && matchesCategory;
  });

  return (
    <Box
      component="section"
      id="products-section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        bgcolor: themeTokens.bgMain,
        borderBottom: `1px solid ${themeTokens.cardBorder}`,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "flex-end" },
            justifyContent: "space-between",
            mb: { xs: 4, sm: 6 },
            gap: 1.5,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: themeTokens.fontSerif,
                  fontWeight: 600,
                  color: themeTokens.textPrimary,
                  fontSize: { xs: "1.85rem", sm: "2.3rem", md: "2.6rem" },
                  lineHeight: 1.15,
                }}
              >
                Our Products
              </Typography>
              <Box
                sx={{
                  bgcolor: themeTokens.accentLight,
                  color: themeTokens.accent,
                  px: 1.5,
                  py: 0.35,
                  borderRadius: 999,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                }}
              >
                In Stock
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: themeTokens.textSecondary,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                mt: 0.75,
              }}
            >
              Elegance meets everyday wellness. Direct warehouse pricing and tested quality.
            </Typography>
          </Box>

          {/* Active filter badge if filtering */}
          {(searchQuery || (selectedCategory && selectedCategory !== "All")) && (
            <Button
              variant="outlined"
              size="small"
              onClick={onResetFilter}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontSize: "0.75rem",
                color: themeTokens.accent,
                borderColor: themeTokens.accent,
              }}
            >
              Clear Filter
            </Button>
          )}
        </Box>

        {/* Empty State */}
        {filteredProducts.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 8, md: 12 },
              px: 3,
              borderRadius: 4,
              bgcolor: themeTokens.cardBg,
              border: `1px dashed ${themeTokens.cardBorder}`,
            }}
          >
            <ShoppingBagIcon sx={{ fontSize: 64, color: themeTokens.textSecondary, mb: 1.5, opacity: 0.5 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: themeTokens.fontSerif,
                fontWeight: 600,
                color: themeTokens.textPrimary,
                mb: 0.5,
              }}
            >
              No products found
            </Typography>
            <Typography variant="body2" sx={{ color: themeTokens.textSecondary, mb: 3 }}>
              {searchQuery || selectedCategory !== "All"
                ? "Try searching for another keyword or clearing your category filter."
                : "No products available in this store yet."}
            </Typography>
            {(searchQuery || selectedCategory !== "All") && onResetFilter && (
              <Button
                variant="contained"
                onClick={onResetFilter}
                sx={{
                  bgcolor: themeTokens.accent,
                  color: "#FFFFFF",
                  borderRadius: 999,
                  textTransform: "none",
                  px: 3,
                }}
              >
                View All Products
              </Button>
            )}
          </Box>
        ) : (
          /* Responsive 4-Column Product Grid */
          <Grid container spacing={{ xs: 2.5, sm: 3, lg: 3.5 }}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} lg={3} key={product._id}>
                <StoreProductCard
                  product={product}
                  onOrder={handleOrder}
                  storePhone={storePhone}
                  storeName={storeName}
                  themeTokens={themeTokens}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default StoreProductPage;
